// Recover from "stale chunk" crashes after a redeploy.
//
// The app is code-split: each screen is a hashed JS chunk loaded on demand via
// import() (React.lazy). When a new version is deployed, the chunk filenames get
// new hashes and the old files are removed. A browser tab that was already open
// still holds the OLD index.html, which references the OLD chunk hashes. The next
// time it lazy-loads a screen, the request for e.g. /assets/PracticeSchedule-<old
// hash>.js 404s — and because the Worker's asset binding is in single-page-app
// mode, the 404 is answered with index.html (text/html). The browser refuses to
// execute HTML as a module ("Expected a JavaScript module … MIME type text/html"),
// the import() rejects, and with no recovery the React tree unmounts → black page.
//
// Fix: detect the failed dynamic import and reload once. A fresh navigation
// re-fetches index.html (served revalidated), which references the current chunk
// hashes, and the screen loads normally.

const RELOAD_KEY = 'lightning:chunk-reload-at';
// Only auto-reload once per window, so a chunk that 404s for a NON-deploy reason
// (genuinely broken build, asset host down) can't trap the tab in a reload loop.
const RELOAD_WINDOW_MS = 15_000;

/** True if the error looks like a failed dynamic-import / module-script load. */
export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|failed to load module script|dynamically imported module/i.test(msg);
}

/**
 * Reload to pick up the current deploy's chunks. Guarded against loops: at most
 * one auto-reload per RELOAD_WINDOW_MS. Returns true if a reload was triggered,
 * false if suppressed (the caller should surface an error instead).
 */
export function reloadForStaleChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? '0');
    if (Date.now() - last < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage blocked (private mode / sandbox) — fall through and reload
    // once; without the guard a true loop is unlikely for a transient stale chunk.
  }
  window.location.reload();
  return true;
}
